import type { ChangeEventHandler } from "react";
import { useId } from "react";
import {
	type Control,
	type FieldPath,
	type FieldValues,
	useController,
	useWatch,
} from "react-hook-form";

import AvatarIcon from "../Avatar";

import CameraCircle from "@/components/icons/CameraCircle";
import FallbackAvatarIcon from "@/components/icons/FallbackAvatar";
import { useToast } from "@/components/ui/use-toast";

type Props<T extends FieldValues> = {
	name: FieldPath<T>;
	control: Control<T>;
	border?: boolean;
};

function AvatarInput<T extends FieldValues>({ control, name, border = true }: Props<T>) {
	const avatar = useWatch({ control, name });
	const { field } = useController({ control, name });
	const inputId = useId();
	const { toast } = useToast();

	const onChange: ChangeEventHandler<HTMLInputElement> = async ({ target }) => {
		if (!target || !target.files || target.files.length === 0) return;
		const file = target.files[0];
		
		// Validate file size (max 5MB)
		const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
		if (file.size > MAX_FILE_SIZE) {
			toast({
				variant: "destructive",
				title: "File too large",
				description: `The image size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the maximum limit of 5MB. Please select a smaller image.`,
			});
			target.value = ''; // Clear the input
			return;
		}
		
		// Convert File to data URL immediately to avoid blob URL issues
		// This ensures the file data is preserved and can be used later
		// Data URLs don't expire like blob URLs, preventing "Failed to fetch" errors
		try {
			const reader = new FileReader();
			const dataUrl = await new Promise<string>((resolve, reject) => {
				reader.onload = (e) => {
					if (e.target?.result && typeof e.target.result === 'string') {
						resolve(e.target.result);
					} else {
						reject(new Error('Failed to read file'));
					}
				};
				reader.onerror = (e) => {
					reject(new Error(`Failed to read file: ${reader.error?.message || 'Unknown error'}`));
				};
				reader.readAsDataURL(file);
			});
			
			field.onChange(dataUrl);
		} catch (error) {
			console.error('Error converting file to data URL:', error);
			// If data URL conversion fails, we still fallback to blob URL as last resort
			// However, blob URLs can expire, so the upload should happen quickly
			// The improved error handling in UpdateUserForm will catch expired blob URLs
			try {
				const blobUrl = URL.createObjectURL(file);
				field.onChange(blobUrl);
				console.warn('Fell back to blob URL - upload should happen immediately to avoid expiration');
			} catch (blobError) {
				console.error('Failed to create blob URL as fallback:', blobError);
				// If both fail, clear the field so user can try again
				field.onChange(undefined);
			}
		}
	};

	return (
		<div className="relative p-2">
			<AvatarIcon avatarUrl={avatar} fallback={<FallbackAvatarIcon />} border={border} />

			<label
				htmlFor={inputId}
				className="absolute bottom-3 right-1 z-20 flex size-7 cursor-pointer items-center justify-center rounded-full"
			>
				<input 
					id={inputId}
					name={String(name)}
					type="file" 
					accept="image/*"
					hidden 
					onChange={onChange} 
					aria-label="Upload avatar image"
				/>
				<CameraCircle className="size-7" />
			</label>
		</div>
	);
}

export default AvatarInput;
